import { useEffect, useRef, useState, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { toast } from 'react-toastify';
import './RouteControl.css';

const RouteControl = ({ position = 'bottomright', projectLocation }) => {
  const map = useMap();
  const [isActive, setIsActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Refs pour stocker les éléments de la carte
  const userMarkerRef = useRef(null);
  const routeLayerRef = useRef(null);
  const watchIdRef = useRef(null);
  const locateControlRef = useRef(null);
  const buttonRef = useRef(null);
  const lastPositionRef = useRef(null);
  const googleMapsButtonRef = useRef(null);

  // Trouver le locate control au montage
  useEffect(() => {
    const controls = document.getElementsByClassName('leaflet-control-locate');
    if (controls.length > 0) {
      locateControlRef.current = controls[0];
    }
  }, []);

  // Créer le marqueur utilisateur
  const createUserMarker = useCallback((position) => {
    const userIcon = L.divIcon({
      className: 'user-location-marker',
      html: `
        <div class="user-location-marker-inner">
          <div class="user-location-marker-circle"></div>
        </div>
      `,
      iconSize: [24, 24],
      iconAnchor: [12, 12]
    });

    return L.marker([position.coords.latitude, position.coords.longitude], {
      icon: userIcon,
      zIndexOffset: 1000
    });
  }, []);

  // Calculer et afficher l'itinéraire
  const calculateRoute = useCallback(async (position) => {
    try {
      const userLocation = [position.coords.latitude, position.coords.longitude];
      lastPositionRef.current = userLocation;

      const response = await fetch(
        `https://router.project-osrm.org/route/v1/driving/${userLocation[1]},${userLocation[0]};${projectLocation[1]},${projectLocation[0]}?overview=full&geometries=geojson`
      );
      
      const data = await response.json();
      
      if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
        const route = data.routes[0];
        
        // Supprimer l'ancien itinéraire s'il existe
        if (routeLayerRef.current) {
          map.removeLayer(routeLayerRef.current);
        }

        // Créer et afficher le nouvel itinéraire
        const routeLayer = L.geoJSON(route.geometry, {
          style: {
            color: '#3388ff',
            weight: 6,
            opacity: 0.6
          }
        });

        routeLayer.addTo(map);
        routeLayerRef.current = routeLayer;

        // Ajuster la vue pour voir l'itinéraire complet
        const bounds = routeLayer.getBounds();
        map.fitBounds(bounds, { padding: [50, 50] });

        const distance = (route.distance / 1000).toFixed(1);
        toast.success(`Distance par la route : ${distance} km`);

        return true;
      }
      
      toast.error("Impossible de calculer l'itinéraire.");
      return false;
    } catch (error) {
      console.error('Erreur lors du calcul de l\'itinéraire:', error);
      toast.error("Une erreur est survenue lors du calcul de l'itinéraire.");
      return false;
    }
  }, [map, projectLocation]);

  // Mettre à jour la position et recalculer l'itinéraire si nécessaire
  const updatePosition = useCallback((position) => {
    const newLatLng = [position.coords.latitude, position.coords.longitude];

    // Mettre à jour le marqueur
    if (userMarkerRef.current) {
      userMarkerRef.current.setLatLng(newLatLng);
    }

    // Recalculer l'itinéraire si on s'est déplacé de plus de 100m
    if (lastPositionRef.current) {
      const distance = map.distance(newLatLng, lastPositionRef.current);
      if (distance > 100) {
        calculateRoute(position);
      }
    }
  }, [map, calculateRoute]);

  // Fonction pour s'assurer que le bouton Google Maps est présent
  const ensureGoogleMapsButton = useCallback(() => {
    if (!projectLocation) return;

    // Si le bouton n'existe pas, le créer
    if (!googleMapsButtonRef.current) {
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${projectLocation[0]},${projectLocation[1]}`;
      const googleMapsButton = L.control({ position: 'bottomright' });
      googleMapsButton.onAdd = function() {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
        const button = L.DomUtil.create('button', 'google-maps-button', container);
        button.innerHTML = '🗺️';
        button.title = 'Ouvrir dans Google Maps';
        L.DomEvent.on(button, 'click', () => {
          window.open(googleMapsUrl, '_blank');
        });
        return container;
      };
      googleMapsButton.addTo(map);
      googleMapsButtonRef.current = googleMapsButton;
    }
  }, [map, projectLocation]);

  // Arrêter le suivi et nettoyer
  const stopTracking = useCallback(async () => {
    if (!isActive) return;

    try {
      setIsProcessing(true);

      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }

      if (routeLayerRef.current) {
        map.removeLayer(routeLayerRef.current);
        routeLayerRef.current = null;
      }
      
      if (userMarkerRef.current) {
        map.removeLayer(userMarkerRef.current);
        userMarkerRef.current = null;
      }

      // Ne pas supprimer le bouton Google Maps ici car il doit rester visible
      // même quand l'itinéraire est arrêté

      if (locateControlRef.current) {
        const locateButton = locateControlRef.current.querySelector('.leaflet-control-locate-location-arrow');
        if (locateButton && locateButton.classList.contains('active')) {
          locateButton.click();
        }
      }

      lastPositionRef.current = null;
      
      setIsActive(false);

      // S'assurer que le bouton Google Maps est toujours présent
      ensureGoogleMapsButton();
    } catch (error) {
      console.error('Erreur lors de l\'arrêt du suivi:', error);
      setIsActive(false);
    } finally {
      setIsProcessing(false);
    }
  }, [map, isActive, ensureGoogleMapsButton]);

  // Démarrer le suivi
  const startTracking = useCallback(async (position) => {
    if (isActive || isProcessing) return;
    
    try {
      setIsProcessing(true);

      // Fermer la popup du projet
      map.closePopup();

      userMarkerRef.current = createUserMarker(position);
      userMarkerRef.current.addTo(map);

      const success = await calculateRoute(position);

      if (success) {
        if (locateControlRef.current) {
          const locateButton = locateControlRef.current.querySelector('.leaflet-control-locate-location-arrow');
          if (locateButton && !locateButton.classList.contains('active')) {
            locateButton.click();
          }
        }

        watchIdRef.current = navigator.geolocation.watchPosition(
          updatePosition,
          (error) => {
            stopTracking();
            if (error.code === 1) {
              toast.error("L'accès à la géolocalisation a été refusé. Veuillez autoriser l'accès dans les paramètres de votre navigateur.");
            } else if (error.code === 2) {
              toast.error("La position n'a pas pu être déterminée. Veuillez vérifier votre connexion GPS.");
            } else if (error.code === 3) {
              toast.error("Le temps de réponse a dépassé la limite. Veuillez réessayer.");
            } else {
              toast.error("Une erreur est survenue lors du suivi de votre position.");
            }
          },
          {
            enableHighAccuracy: true,
            maximumAge: 0,
            timeout: 5000
          }
        );

        setIsActive(true);
      } else {
        await stopTracking();
      }
    } catch (error) {
      await stopTracking();
    } finally {
      setIsProcessing(false);
    }
  }, [map, isActive, isProcessing, createUserMarker, calculateRoute, updatePosition, stopTracking]);

  // Gérer le bouton Google Maps
  useEffect(() => {
    if (!projectLocation) {
      // Supprimer le bouton si aucun projet n'est sélectionné
      if (googleMapsButtonRef.current) {
        map.removeControl(googleMapsButtonRef.current);
        googleMapsButtonRef.current = null;
      }
      return;
    }

    // Supprimer le bouton précédent s'il existe
    if (googleMapsButtonRef.current) {
      map.removeControl(googleMapsButtonRef.current);
      googleMapsButtonRef.current = null;
    }

    // Créer le nouveau bouton
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${projectLocation[0]},${projectLocation[1]}`;
    const googleMapsButton = L.control({ position: 'bottomright' });
    googleMapsButton.onAdd = function() {
      const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
      const button = L.DomUtil.create('button', 'google-maps-button', container);
      button.innerHTML = '🗺️';
      button.title = 'Ouvrir dans Google Maps';
      L.DomEvent.on(button, 'click', () => {
        window.open(googleMapsUrl, '_blank');
      });
      return container;
    };
    googleMapsButton.addTo(map);
    googleMapsButtonRef.current = googleMapsButton;

    return () => {
      if (googleMapsButtonRef.current) {
        map.removeControl(googleMapsButtonRef.current);
        googleMapsButtonRef.current = null;
      }
    };
  }, [map, projectLocation]);

  // Créer et gérer le bouton de contrôle
  useEffect(() => {
    if (!projectLocation) return;

    const routeControl = L.Control.extend({
      options: {
        position: position
      },

      onAdd: function() {
        const container = L.DomUtil.create('div', 'leaflet-bar');
        const button = L.DomUtil.create('button', 'route-button', container);
        
        buttonRef.current = button;
        button.title = 'Activer l\'itinéraire et le suivi';
        button.innerHTML = '<span class="route-icon">➜</span>';
        
        const handleClick = async (e) => {
          e.preventDefault();
          e.stopPropagation();

          if (isProcessing) return;

          try {
            if (isActive) {
              await stopTracking();
            } else {
              if (!projectLocation) {
                toast.warning("Veuillez d'abord sélectionner un projet.");
                return;
              }

              const position = await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(
                  resolve,
                  reject,
                  {
                    enableHighAccuracy: true,
                    timeout: 5000,
                    maximumAge: 0
                  }
                );
              });
              
              await startTracking(position);
            }
          } catch (error) {
            if (error.code === 1) {
              toast.error("Vous avez refusé l'accès à la géolocalisation. Veuillez autoriser l'accès dans les paramètres de votre navigateur.");
            } else {
              toast.error("Une erreur est survenue.");
            }
          }
        };

        L.DomEvent.on(button, 'click', handleClick);
        return container;
      }
    });

    const control = new routeControl();
    map.addControl(control);

    if (buttonRef.current) {
      if (isActive) {
        buttonRef.current.classList.add('active');
      } else {
        buttonRef.current.classList.remove('active');
      }
    }

    return () => {
      map.removeControl(control);
    };
  }, [map, position, projectLocation, isActive, isProcessing, startTracking, stopTracking]);

  // Nettoyage au démontage
  useEffect(() => {
    return () => {
      if (isActive) {
        stopTracking();
      }
    };
  }, [isActive, stopTracking]);

  return null;
};

export default RouteControl; 